/**
 * La demostración — one conversion rule, shown once, before the ladder is in play.
 *
 * ## Why this exists
 *
 * Reported from a real play session: *"This game is a glorified flash card."* Measured, the
 * complaint was fair. Tiers open on rolling accuracy (invariant 4), tier-3 and tier-4 draws are
 * capped at 25% of a week (invariant 9), and the two together put Conversión — the one mechanic
 * here that is not a flashcard — around a fortnight behind the first launch. The most interesting
 * thing this game does was the last thing it did.
 *
 * So: one rule, in the first ninety seconds, before anything is gated. `-tion` becomes `-ción`,
 * and then a word the learner has never been taught, which he can nonetheless produce.
 *
 * ## What it deliberately is not
 *
 * **Not a tier-4 draw, and not an unlock.** Gating is untouched: no accuracy is recorded, no tier
 * opens, tier 4 arrives exactly when it would have. Invariant 4 says gating is on measured rolling
 * accuracy and never on items seen — this measures nothing, so there is nothing for it to gate on.
 *
 * **Not scored.** No `Attempt` is written, no FSRS interval moves, and nothing counts toward the
 * grammar share (invariant 9). The app already has a production step that scores nothing — the
 * forced encoding after a second failure (invariant 5) — and this is the second. The value is in
 * producing the word, not in filing the result.
 *
 * **Not skippable into an advantage.** There is nothing to gain by dismissing it and nothing lost
 * by getting it wrong, so the reveal is offered outright rather than held behind a cost.
 *
 * Pure. No DOM, no clock, no store.
 */

import { grade } from '../lang/validator';
import type { LearnerProfile } from '../store/profile';

/** The rule the demonstration teaches. `-ción` is the largest productive class Spanish has. */
export const DEMO_RULE_ID = 'cion';

/** The word it asks for. Never taught before this card, and derivable from the rule alone. */
export const DEMO_ITEM_ID = 'conv-cion-situacion';

export interface DemostracionInput {
  /** `ConversionRule.name` — the headline, e.g. `-tion becomes -ción`. */
  ruleName: string;
  /** The rule's own note from the corpus: gender, stress, what happens in the plural. */
  ruleNote: string;
  /** The English prompt, e.g. `situation`. */
  en: string;
  /** The Spanish, correctly accented. */
  es: string;
  /** What the learner typed. Null before the first submission. */
  typed: string | null;
  /** Set when the reveal was taken instead of an answer. */
  revealed: boolean;
}

export interface DemostracionView {
  label: string;
  ruleName: string;
  ruleNote: string;
  en: string;
  /** Null until answered — this is the whole question. */
  answer: string | null;
  /** The line under the answer, once there is one. */
  verdict: string | null;
  /** The point of the card, shown only once the word has been produced. */
  claim: string | null;
  correct: boolean | null;
  announce: string;
}

/**
 * The claim, and the reason the card exists.
 *
 * Deliberately a **count**, not praise. "Well done" would be the app congratulating him for its own
 * rule; a number is a fact about Spanish that happens to be good news, and it is checkable.
 */
export const DEMO_CLAIM =
  'You have not been taught that word. The rule produced it — and it produces around a thousand ' +
  'more, which is why this game spends a quarter of its time on rules and never more.';

/** The line when the reveal was taken. States what happened and makes no comment on it. */
export const DEMO_REVEALED =
  'The rule produces it either way. It will come back as an ordinary item, and so will the rest ' +
  'of its class.';

export function demostracionView(input: DemostracionInput): DemostracionView {
  const label = 'Before the first ronda';

  if (input.revealed) {
    return {
      label,
      ruleName: input.ruleName,
      ruleNote: input.ruleNote,
      en: input.en,
      answer: input.es,
      verdict: null,
      claim: DEMO_REVEALED,
      correct: null,
      announce: `${input.en} is ${input.es}.`,
    };
  }

  if (input.typed === null) {
    return {
      label,
      ruleName: input.ruleName,
      ruleNote: input.ruleNote,
      en: input.en,
      answer: null,
      verdict: null,
      claim: null,
      correct: null,
      announce: `${label}. ${input.ruleName}. Now the Spanish for ${input.en}.`,
    };
  }

  /*
   * Graded by the same validator every other card uses, so a missing accent is a missing accent
   * here too — `situacion` is correct and told about its accent, not marked wrong.
   */
  const result = grade({ expected: input.es, actual: input.typed });
  return {
    label,
    ruleName: input.ruleName,
    ruleNote: input.ruleNote,
    en: input.en,
    answer: input.es,
    verdict: result.message,
    // The claim is only true if he produced it. Saying it after a wrong answer would be the app
    // telling him he did something he did not do.
    claim: result.correct ? DEMO_CLAIM : DEMO_REVEALED,
    correct: result.correct,
    announce: `${result.message} ${input.es}.`,
  };
}

/**
 * Has the demonstration already happened?
 *
 * Recorded on the profile rather than inferred from the attempt log, for the reason `introducedAt`
 * exists (D-213): inference was wrong for every learner whose store predated the feature, and an
 * inferred "already seen" is indistinguishable from a bug.
 */
export function demoDue(profile: Pick<LearnerProfile, 'demoSeenAt'>): boolean {
  return profile.demoSeenAt === undefined;
}
